// RightPanel.js - Component Details Panel

import React from 'react';
import styled from 'styled-components';
import useStore from '../../store/useStore';
import THEME from '../../styles/theme';

const RightPanel = () => {
  const {
    selectedNodeId,
    nodes,
    calculationResults,
    frequencyMode,
    sweepResults,
    openEditModal
  } = useStore();

  const selectedNode = nodes.find(n => n.id === selectedNodeId);
  const nodeResults = selectedNodeId ? calculationResults[selectedNodeId] : null;

  if (sweepResults) {
    return (
      <Container>
        <Title>Frequency Sweep Results</Title>

        <Section>
          <SectionTitle>Sweep Parameters</SectionTitle>
          <InfoRow>
            <Label>Frequency Range:</Label>
            <Value>{sweepResults.frequencies[0].toFixed(1)} - {sweepResults.frequencies[sweepResults.frequencies.length - 1].toFixed(1)} MHz</Value>
          </InfoRow>
          <InfoRow>
            <Label>Points:</Label>
            <Value>{sweepResults.frequencies.length}</Value>
          </InfoRow>
        </Section>

        <Section>
          <SectionTitle>Results Summary</SectionTitle>
          <InfoRow>
            <Label>Gain (Avg):</Label>
            <Value>{(sweepResults.gain.reduce((a, b) => a + b, 0) / sweepResults.gain.length).toFixed(2)} dB</Value>
          </InfoRow>
          <InfoRow>
            <Label>NF (Avg):</Label>
            <Value>{(sweepResults.noiseFigure.reduce((a, b) => a + b, 0) / sweepResults.noiseFigure.length).toFixed(2)} dB</Value>
          </InfoRow>
        </Section>

        <Button onClick={() => useStore.getState().setSweepResults(null)}>
          Close
        </Button>
      </Container>
    );
  }

  if (!selectedNode) {
    return (
      <Container>
        <Title>Component Details</Title>
        <EmptyMessage>Select a component to view details</EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Title>Component Details</Title>

      <Section>
        <SectionTitle>{selectedNode.data.name || selectedNode.type}</SectionTitle>
        <InfoRow>
          <Label>Type:</Label>
          <Value>{selectedNode.type}</Value>
        </InfoRow>
        <InfoRow>
          <Label>ID:</Label>
          <Value style={{ fontSize: '10px' }}>{selectedNode.id}</Value>
        </InfoRow>
      </Section>

      <Section>
        <SectionTitle>Parameters</SectionTitle>
        {Object.entries(selectedNode.data.parameters || {}).map(([key, param]) => (
          param.displayOnCard && (
            <InfoRow key={key}>
              <Label>{param.name}:</Label>
              <Value>{param.value} {param.unit}</Value>
            </InfoRow>
          )
        ))}
        <Button onClick={() => openEditModal(selectedNode.id)}>
          Edit Parameters
        </Button>
      </Section>

      {nodeResults && (
        <Section>
          <SectionTitle>Calculated Results</SectionTitle>
          <InfoRow>
            <Label>Input Power:</Label>
            <Value>{nodeResults.inputPower} dBm</Value>
          </InfoRow>
          <InfoRow>
            <Label>Output Power:</Label>
            <Value>{nodeResults.outputPower} dBm</Value>
          </InfoRow>
          <InfoRow>
            <Label>Gain:</Label>
            <Value>{nodeResults.gain} dB</Value>
          </InfoRow>
          <InfoRow>
            <Label>Cumulative Gain:</Label>
            <Value>{nodeResults.cumulativeGain} dB</Value>
          </InfoRow>
          <InfoRow>
            <Label>Cumulative NF:</Label>
            <Value>{nodeResults.cumulativeNF} dB</Value>
          </InfoRow>
          {nodeResults.compression !== '0.000' && (
            <InfoRow>
              <Label>Compression:</Label>
              <Value style={{ color: THEME.warning }}>
                {nodeResults.compression} dB
              </Value>
            </InfoRow>
          )}
          {nodeResults.iip3 !== 'N/A' && (
            <InfoRow>
              <Label>IIP3:</Label>
              <Value>{nodeResults.iip3} dBm</Value>
            </InfoRow>
          )}
          {nodeResults.oip3 !== 'N/A' && (
            <InfoRow>
              <Label>OIP3:</Label>
              <Value>{nodeResults.oip3} dBm</Value>
            </InfoRow>
          )}
        </Section>
      )}

      {calculationResults['path_0'] && (
        <Section>
          <SectionTitle>System Summary</SectionTitle>
          <InfoRow>
            <Label>Total Gain:</Label>
            <Value>{calculationResults['path_0'].totalGain} dB</Value>
          </InfoRow>
          <InfoRow>
            <Label>System NF:</Label>
            <Value>{calculationResults['path_0'].noiseFigure} dB</Value>
          </InfoRow>
          <InfoRow>
            <Label>Output Power:</Label>
            <Value>{calculationResults['path_0'].outputPower} dBm</Value>
          </InfoRow>
          {calculationResults['path_0'].oip3 !== 'N/A' && (
            <InfoRow>
              <Label>System OIP3:</Label>
              <Value>{calculationResults['path_0'].oip3} dBm</Value>
            </InfoRow>
          )}
        </Section>
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 320px;
  background: ${THEME.sidebarBackground};
  border-left: 1px solid #404040;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: ${THEME.spacing.lg};
`;

const Title = styled.h2`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.xl};
  margin-bottom: ${THEME.spacing.lg};
  padding-bottom: ${THEME.spacing.md};
  border-bottom: 1px solid #404040;
`;

const Section = styled.div`
  margin-bottom: ${THEME.spacing.lg};
  padding-bottom: ${THEME.spacing.lg};
  border-bottom: 1px solid #333;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.lg};
  margin-bottom: ${THEME.spacing.md};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${THEME.spacing.sm};
`;

const Label = styled.span`
  color: ${THEME.textSecondary};
  font-size: ${THEME.fontSize.sm};
`;

const Value = styled.span`
  color: ${THEME.textPrimary};
  font-size: ${THEME.fontSize.sm};
  font-weight: 600;
`;

const EmptyMessage = styled.div`
  color: ${THEME.textMuted};
  font-size: ${THEME.fontSize.md};
  text-align: center;
  margin-top: ${THEME.spacing.xxl};
`;

const Button = styled.button`
  width: 100%;
  background: ${THEME.buttonPrimary};
  color: ${THEME.textPrimary};
  padding: ${THEME.spacing.sm};
  border-radius: ${THEME.borderRadiusSmall};
  font-size: ${THEME.fontSize.sm};
  margin-top: ${THEME.spacing.md};
  transition: background ${THEME.transitionFast};

  &:hover {
    background: ${THEME.buttonHover};
  }
`;

export default RightPanel;
